import React, { useEffect, useMemo, useRef, useState } from "react"

import { FeatureFlag } from "@common-shared/config/featureFlags.config.ts"
import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config.ts"
import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config.ts"
import { IPolicyColumnConfiguration } from "@common-shared/types/policies.types.ts"

import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element.tsx"
import { SpinnerElement } from "@front-shared/elements/spinner.element.tsx"
import { useScrollFix } from "@front-shared/hooks/scrollFix.hook.ts"

import { GENERAL_CONFIG } from "../../../config/general.config"
import {
    POLICIES_CONFIG,
    PolicyHeaderDragDirection,
} from "../../../config/policies.config"
import { ROUTES_CONFIG } from "../../../config/routes.config"
import { PoliciesHelper } from "../../../helpers/policies.helper"
import { PoliciesListHelper } from "../../../helpers/policiesList.helper"
import { useHasPermissions } from "../../../hooks/hasPermissions.hook"
import { useIsFeatureFlagEnabled } from "../../../hooks/isFeatureFlagEnabled.hook"
import { usePoliciesList } from "../../../hooks/policiesList.hook"
import { useDemoMode } from "../../../hooks/useDemoMode.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { BulkOverridesComponent } from "../overrides/bulkOverrides/bulkOverrides.component"
import { PoliciesListComponent } from "../policiesList/policiesList.component"
import { PoliciesListHeaderComponent } from "../policiesList/policiesListHeader.component"
import { VisualizationWrapperComponent } from "../visualizationWrapper.component"
import { BulkPolicyControlComponent } from "./bulkPolicyControl.component"
import { ReportsFlagsVisualizationComponent } from "./reportsFlagsVisualization.component"
import { ReportsStatusCodesVisualizationComponent } from "./reportsStatusCodesVisualization.component"

export const ReportsComponent: React.FunctionComponent = () => {
    useScrollFix()

    const { isDemo } = useDemoMode()

    useHasPermissions(OneVerifyPermission.viewHistoricalCoverageChecks)

    useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_GENERAL_REPORTS,
        undefined,
        ROUTES_CONFIG.profileUrl,
    )

    const isReportVisualizationEnabled = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_REPORTS_VISUALIZATION,
    )

    const {
        // vars
        isOverridesEnabled,
        canManageOverrides,
        // state
        selectedPolicies,
        setSelectedPolicies,
        // functions
        onPoliciesSelected,
        onPoliciesDeselected,
        onDeselectAllPolicies,
    } = usePoliciesList()

    const scrollableTableRef = useRef<HTMLDivElement>(null)

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    const [isBulkOverrideActive, setIsBulkOverrideActive] =
        useState<boolean>(false)

    // This component controls them and passes to list for display and header for modifications
    const [columnsConfiguration, setColumnsConfiguration] = useState<
        IPolicyColumnConfiguration[]
    >([])

    const listPoliciesSelectable = useMemo<boolean>(() => {
        if (isDemo) {
            return false
        }

        return !!(canManageOverrides && isOverridesEnabled)
    }, [isDemo, isOverridesEnabled, canManageOverrides])

    const showBulkOverrideButton = useMemo<boolean>(() => {
        if (isDemo) {
            return false
        }

        return !!(isOverridesEnabled && canManageOverrides)
    }, [isDemo, isOverridesEnabled, canManageOverrides])

    // Change columns configurations once practice role modalities change
    useEffect(() => {
        if (!selectedPracticeRole) {
            return
        }

        setColumnsConfiguration(
            PoliciesListHelper.getColumnsConfiguration(
                PoliciesListType.REPORTS,
                selectedPracticeRole.availableModalities,
            ),
        )
    }, [selectedPracticeRole?.availableModalities])

    const onSaveColumnsConfiguration = (
        columns: IPolicyColumnConfiguration[],
    ) => {
        setColumnsConfiguration(columns)

        PoliciesListHelper.saveColumnsConfigurations(
            PoliciesListType.REPORTS,
            columns,
            selectedPracticeRole?.availableModalities,
        )
    }

    const onChangeColumnOrder = (
        columnLabelMoveSource: string,
        columnLabelMoveTarget: string,
        direction: PolicyHeaderDragDirection,
    ) => {
        const newColumns = PoliciesListHelper.changeSingleColumnOrdering(
            columnsConfiguration,
            columnLabelMoveSource,
            columnLabelMoveTarget,
            direction,
        )

        onSaveColumnsConfiguration(newColumns)
    }

    const onCancelBulkOverride = () => {
        setIsBulkOverrideActive(false)
        setSelectedPolicies([])
    }

    const onBulkOverride = () => {
        setSelectedPolicies(current => {
            return current.filter(policy => {
                return PoliciesHelper.isPolicyBulkOverrideEligible(policy)
            })
        })

        setIsBulkOverrideActive(true)
    }

    return (
        <div data-produktly="reports-general-page" className="pb-32 relative">
            <PageHelmetElement
                title={
                    POLICIES_CONFIG.headerTitleMapping[PoliciesListType.REPORTS]
                }
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            {selectedPracticeRole ? (
                <>
                    <PoliciesListHeaderComponent
                        className="mb-[24px]"
                        listType={PoliciesListType.REPORTS}
                        setSelectedPolicies={setSelectedPolicies}
                        scrollableTableRef={scrollableTableRef}
                        columnsConfiguration={columnsConfiguration}
                        onColumnsConfigurationChange={
                            onSaveColumnsConfiguration
                        }
                        withExport
                        withSavingFilterPresets
                    />

                    {isReportVisualizationEnabled ? (
                        <VisualizationWrapperComponent
                            adjustHorizontalScrollReference={scrollableTableRef}
                            listType={PoliciesListType.REPORTS}
                        >
                            <ReportsStatusCodesVisualizationComponent
                                className="
                                    w-[350px] self-stretch
                                    border-r border-solid border-brand-warmShadow
                                "
                            />

                            <ReportsFlagsVisualizationComponent className="flex-1 self-stretch" />
                        </VisualizationWrapperComponent>
                    ) : null}

                    {columnsConfiguration.length ? (
                        <div data-produktly="reports-general-results">
                            <PoliciesListComponent
                                scrollableElementRef={scrollableTableRef}
                                listType={PoliciesListType.REPORTS}
                                sortEnabled={true}
                                columns={columnsConfiguration}
                                policiesSelectable={listPoliciesSelectable}
                                selectedItems={selectedPolicies}
                                onSelectedItems={onPoliciesSelected}
                                onDeselectedItems={onPoliciesDeselected}
                                onDeselectedAllItems={onDeselectAllPolicies}
                                onChangeColumnOrder={onChangeColumnOrder}
                                isSinglePolicySelectable={
                                    PoliciesHelper.isPolicyBulkOverrideEligible
                                }
                            />
                        </div>
                    ) : null}

                    {listPoliciesSelectable ? (
                        <>
                            <BulkPolicyControlComponent
                                listType={PoliciesListType.REPORTS}
                                onCancelBulkPolicyControl={
                                    onDeselectAllPolicies
                                }
                                coverageChecks={selectedPolicies}
                                setCoverageChecks={setSelectedPolicies}
                                quotaUsageType={QuotaUsageType.GENERAL}
                                withBulkOverride={showBulkOverrideButton}
                                bulkOverrideLabel="Bulk Override"
                                onBulkOverride={onBulkOverride}
                            />

                            {isBulkOverrideActive ? (
                                <BulkOverridesComponent
                                    listType={PoliciesListType.REPORTS}
                                    onCancelBulkOverride={onCancelBulkOverride}
                                    coverageChecksToOverride={selectedPolicies}
                                    setCoverageChecksToOverride={
                                        setSelectedPolicies
                                    }
                                />
                            ) : null}
                        </>
                    ) : null}
                </>
            ) : (
                <SpinnerElement containerClassName="mt-[32px] text-center" />
            )}
        </div>
    )
}
